import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, MessagesSquare } from "lucide-react";
import { PageHero } from "./PageHero";
import { Reveal, Kicker } from "./Reveal";
import { trackEvent } from "../lib/ga";

const buildFaqSchema = (sections) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: sections.flatMap((section) =>
    section.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    }))
  ),
});

export const FaqResourcePage = ({
  pageTestId,
  kicker,
  h1Lines,
  intro,
  primaryCta,
  sections,
  inlineCta,
  closing,
  related = [],
}) => {
  let n = 0;
  return (
    <div data-testid={pageTestId}>
      <script type="application/ld+json">{JSON.stringify(buildFaqSchema(sections))}</script>

      <PageHero kicker={kicker} lines={h1Lines} />

      <section className="py-16 md:py-24" data-testid="faq-intro">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-base md:text-lg text-[#475569] leading-relaxed">{intro}</div>
          </Reveal>

          {primaryCta && (
            <Reveal delay={0.08}>
              <div className="mt-8">
                <Link to={primaryCta.to} data-testid="faq-intro-cta" className="btn-primary">
                  {primaryCta.label} <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
          )}

          <Reveal delay={0.12}>
            <nav
              className="mt-12 flex flex-wrap gap-3 border-t border-[#0A2B3D]/10 pt-8"
              aria-label="Jump to a section"
              data-testid="faq-section-nav"
            >
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  data-testid={`faq-nav-${section.id}`}
                  className="text-xs font-medium tracking-[0.06em] uppercase text-[#0A2B3D] bg-[#EBF3F8] border border-[#A9CFE2]/60 px-4 py-2.5 hover:border-[#3E9BDC] hover:text-[#3E9BDC] transition-colors duration-300"
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </Reveal>
        </div>
      </section>

      {sections.map((section) => (
        <React.Fragment key={section.id}>
          <section
            id={section.id}
            className="pb-16 md:pb-20 scroll-mt-28 border-t border-[#0A2B3D]/10 pt-16 md:pt-20"
            data-testid={`faq-section-${section.id}`}
          >
            <div className="max-w-3xl mx-auto px-5 sm:px-8">
              <Reveal>
                <Kicker>{section.title}</Kicker>
              </Reveal>
              <div className="mt-10 space-y-10">
                {section.items.map((item) => {
                  n += 1;
                  const idx = n;
                  return (
                    <Reveal key={item.q} delay={0.04}>
                      <div data-testid={`faq-item-${idx}`}>
                        <h2 className="font-serif font-bold text-xl md:text-2xl text-[#0A2B3D] leading-snug tracking-tight">
                          {item.q}
                        </h2>
                        <p className="mt-4 text-[#475569] leading-relaxed" data-testid={`faq-answer-${idx}`}>
                          {item.a}
                        </p>
                        {item.link && (
                          <Link
                            to={item.link.to}
                            data-testid={`faq-answer-link-${idx}`}
                            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#3E9BDC] hover:underline underline-offset-4"
                          >
                            {item.link.label} <ArrowRight size={15} />
                          </Link>
                        )}
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </section>

          {inlineCta && inlineCta.afterSectionId === section.id && (
            <section className="pb-16 md:pb-20" data-testid="faq-inline-cta">
              <div className="max-w-3xl mx-auto px-5 sm:px-8">
                <Reveal>
                  <div className="bg-[#EBF3F8] border border-[#A9CFE2]/60 p-8 md:p-10 flex flex-col sm:flex-row sm:items-center gap-6">
                    <div className="flex items-start gap-4 flex-1">
                      <MessagesSquare size={24} className="text-[#3E9BDC] mt-1 shrink-0" />
                      <div>
                        <p className="font-serif font-bold text-lg md:text-xl text-[#0A2B3D] leading-snug">
                          {inlineCta.heading || "Was this helpful?"}
                        </p>
                        <p className="mt-2 text-sm text-[#475569] leading-relaxed">{inlineCta.body}</p>
                      </div>
                    </div>
                    <Link
                      to={inlineCta.to}
                      data-testid="faq-inline-cta-button"
                      onClick={() =>
                        trackEvent("faq_cta_click", {
                          cta: inlineCta.eventName || "faq_inline",
                          source_page: pageTestId,
                        })
                      }
                      className="btn-primary shrink-0 w-fit"
                    >
                      {inlineCta.buttonLabel} <ArrowRight size={16} />
                    </Link>
                  </div>
                </Reveal>
              </div>
            </section>
          )}
        </React.Fragment>
      ))}

      <section className="bg-[#0A2B3D] text-white py-20 md:py-28" data-testid="faq-closing">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Kicker light>{closing.kicker || "Get Started"}</Kicker>
            <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl tracking-tight leading-[1.15]">
              {closing.heading}
            </h2>
            <p className="mt-5 text-white/75 leading-relaxed max-w-2xl">{closing.body}</p>
            <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4">
              <Link to={closing.primary.to} data-testid="faq-closing-cta" className="btn-gold">
                {closing.primary.label} <ArrowRight size={16} />
              </Link>
              {closing.secondary && !closing.secondary.external && (
                <Link
                  to={closing.secondary.to}
                  data-testid="faq-closing-secondary"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white/85 hover:text-[#3E9BDC] transition-colors"
                >
                  {closing.secondary.label} <ArrowRight size={15} />
                </Link>
              )}
              {closing.secondary && closing.secondary.external && (
                <a
                  href={closing.secondary.to}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="faq-closing-secondary"
                  className="inline-flex items-center gap-2 text-sm text-white/85 hover:text-[#3E9BDC] transition-colors"
                >
                  <CalendarClock size={16} /> {closing.secondary.label}
                </a>
              )}
            </div>

            {related.length > 0 && (
              <div className="mt-12 border-t border-white/10 pt-8" data-testid="faq-related-services">
                <p className="eyebrow text-[#A9CFE2] mb-4">Related Services</p>
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  {related.map((r, i) => (
                    <React.Fragment key={r.to}>
                      {i > 0 && <span className="text-white/25">·</span>}
                      <Link
                        to={r.to}
                        data-testid={`faq-related-${i}`}
                        className="text-white/80 hover:text-white transition-colors"
                      >
                        {r.label}
                      </Link>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
};
